import axios from "axios";

export const BACKEND_URL = '';
export const API = '/api';

// Set default axios timeout
axios.defaults.timeout = 60000;

// Auto-attach JWT token to all API requests
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('shweebum_token');
  if (token && (config.url?.includes('/api') || (BACKEND_URL && config.url?.startsWith(BACKEND_URL)))) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Grace window: true within 60s of a successful sign-in (rollout/restart protection)
export const isWithinLoginGrace = () => {
  const loginTs = parseInt(localStorage.getItem('shweebum_login_ts') || '0', 10);
  return !!loginTs && (Date.now() - loginTs) < 60000;
};

// Auto-handle 401 responses — force re-login only for genuine auth failures
let _401Count = 0;
axios.interceptors.response.use(
  (response) => {
    _401Count = 0; // Reset on any successful response
    return response;
  },
  (error) => {
    if (error?.response?.status === 401 && error?.config?.url?.includes('/api/')) {
      const url = error.config.url || '';
      // Don't logout on auth endpoints (login/register return 401 for wrong creds)
      if (url.includes('/auth/login') || url.includes('/auth/register')) {
        return Promise.reject(error);
      }
      
      _401Count++;
      // Grace window: never force-logout within 60s of a successful sign-in.
      // Protects against transient 401s during production rollouts/pod restarts
      // that would otherwise bounce a freshly signed-in user back to the login screen.
      const withinLoginGrace = isWithinLoginGrace();
      // Only force logout after 3+ consecutive 401s (avoids transient issues)
      if (_401Count >= 3 && !withinLoginGrace) {
        const currentPath = window.location.pathname;
        if (currentPath !== '/' && currentPath !== '/pricing') {
          console.warn('[Auth] 3 consecutive 401 errors - logging out');
          localStorage.removeItem('shweebum_user');
          localStorage.removeItem('shweebum_token');
          window.location.href = '/';
        }
        _401Count = 0;
      } else {
        console.warn(`[Auth] 401 error (${_401Count}/3) - not logging out yet`);
      }
    }
    return Promise.reject(error);
  }
);

// Helper: poll manuscript outline job until complete
export async function pollManuscriptOutlines(manuscriptId, userId, onProgress) {
  const startRes = await axios.post(`${API}/manuscript/generate-outlines/${manuscriptId}?user_id=${userId}`, {}, { timeout: 120000 });
  const jobId = startRes.data.job_id;
  if (onProgress) onProgress({ status: "PENDING", current_batch: 0, total_batches: 1 });

  let consecutiveErrors = 0;
  const MAX_ERRORS = 5;

  while (true) {
    await new Promise(r => setTimeout(r, 4000));
    try {
      const statusRes = await axios.get(`${API}/manuscript/outlines-status/${jobId}`, { timeout: 60000 });
      consecutiveErrors = 0;
      const data = statusRes.data;
      if (onProgress) onProgress(data);
      if (data.status === "COMPLETED") return { outlines: data.outlines, chapter_count: data.chapter_count };
      if (data.status === "FAILED") throw new Error(data.error_message || "Outline generation failed");
    } catch (err) {
      // If it's a timeout or network error, retry instead of failing
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout') || err.message?.includes('Network Error')) {
        consecutiveErrors++;
        if (onProgress) onProgress({ status: "PROCESSING", message: `Retrying... (${consecutiveErrors}/${MAX_ERRORS})` });
        if (consecutiveErrors >= MAX_ERRORS) {
          throw new Error("Lost connection to server after multiple retries. Please try again.");
        }
        // Wait longer between retries
        await new Promise(r => setTimeout(r, 5000));
        continue;
      }
      throw err;
    }
  }
}
